import { cn } from "@/lib/utils";
import {
  CONTEST_CHECKLIST_ITEMS,
  checklistLabel,
} from "@/lib/contest-checklist";
import type { ContestChecklistValue } from "@/types/contest-submission";

interface Props {
  /** key → value. 정의된 8개 외의 key(legacy)도 렌더한다. */
  values: Record<string, ContestChecklistValue>;
  onChange: (key: string, value: ContestChecklistValue) => void;
  /** char_count 항목 옆에 표시할 자동 집계 힌트 (예: "12,345자 자동 집계") */
  charCountHint?: string | null;
}

const SEGMENTS: Array<{
  value: ContestChecklistValue;
  label: string;
  activeClass: string;
}> = [
  { value: "O", label: "O", activeClass: "bg-emerald-600 text-white" },
  { value: "X", label: "X", activeClass: "bg-rose-600 text-white" },
  { value: "NA", label: "미정", activeClass: "bg-gray-500 text-white" },
];

/** 형식 요건 체크리스트 — 항목별 O/X/미정 3분할 토글. 완전 controlled. */
export function SubmissionChecklist({ values, onChange, charCountHint }: Props) {
  const knownKeys = new Set(CONTEST_CHECKLIST_ITEMS.map((item) => item.key));
  const unknownKeys = Object.keys(values).filter((key) => !knownKeys.has(key));
  const rows = [
    ...CONTEST_CHECKLIST_ITEMS.map((item) => ({
      key: item.key,
      label: item.label,
      autoHint: item.autoHint,
    })),
    // 항목 정의가 바뀐 뒤 남은 legacy key 도 그대로 보여준다.
    ...unknownKeys.map((key) => ({
      key,
      label: checklistLabel(key),
      autoHint: undefined,
    })),
  ];

  return (
    <div className="space-y-1.5">
      {rows.map((row) => {
        const current = values[row.key] ?? "NA";
        return (
          <div
            key={row.key}
            className="flex items-center justify-between gap-2 py-0.5"
          >
            <div className="min-w-0">
              <span className="text-sm">{row.label}</span>
              {row.autoHint === "charCount" && charCountHint && (
                <span className="ml-1.5 text-xs text-muted-foreground">
                  ({charCountHint})
                </span>
              )}
            </div>
            <div
              className="inline-flex shrink-0 overflow-hidden rounded-md border"
              role="group"
              aria-label={`${row.label} 판정`}
            >
              {SEGMENTS.map((segment) => (
                <button
                  key={segment.value}
                  type="button"
                  aria-pressed={current === segment.value}
                  onClick={() => onChange(row.key, segment.value)}
                  className={cn(
                    "px-2.5 py-1 text-xs font-medium transition-colors border-l first:border-l-0",
                    current === segment.value
                      ? segment.activeClass
                      : "bg-white text-muted-foreground hover:bg-muted",
                  )}
                >
                  {segment.label}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
